import {
  MutableRefObject, useCallback, useEffect, useRef,
} from 'react';
import { useReactToPrint } from 'react-to-print';
import { GridApiPro } from '@mui/x-data-grid-pro';
import { useAppDispatch, useAppSelector } from './useAppStore';
import { getIsPrintMode } from '../store/common/selectors';
import { commonActions } from '../store/common/slice';

export function usePrint(
  printRef: MutableRefObject<HTMLDivElement | null>,
  apiRef?: MutableRefObject<GridApiPro>,
) {
  const isPrintMode = useAppSelector(getIsPrintMode);
  const promiseResolveRef = useRef<(() => void) | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isPrintMode && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrintMode]);

  const updateColumnsOnPrint = useCallback(() => {
    if (apiRef?.current) {
      const allColumns = apiRef.current.getAllColumns();
      allColumns.forEach((column) => {
        const isVisible = column.field !== 'actions';
        apiRef.current.setColumnVisibility(column.field, isVisible);
      });
    }
  }, [apiRef]);

  const restoreColumnsAfterPrint = useCallback(() => {
    if (apiRef?.current) {
      const allColumns = apiRef.current.getAllColumns();
      allColumns.forEach((column) => {
        apiRef.current.setColumnVisibility(column.field, true);
      });
    }
  }, [apiRef]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    removeAfterPrint: true,
    onBeforeGetContent: () => new Promise<void>((resolve: () => void) => {
      updateColumnsOnPrint();
      promiseResolveRef.current = resolve;
      dispatch(commonActions.setIsPrintMode(true));
    }),
    onAfterPrint: () => {
      restoreColumnsAfterPrint();
      promiseResolveRef.current = null;
      dispatch(commonActions.setIsPrintMode(false));
    },
  });

  return {
    handlePrint,
  };
}
